<script setup lang="ts">
import { computed } from 'vue';
import Button from 'primevue/button';

import { UiDialogCloseButton, UiDialogShell } from '@peanuds/ui';
import sessionExpiredDialogIllustration from '../assets/session-expired-dialog-illustration-4b9ec2.png';

interface Props {
  visible: boolean;
  isSignInRedirectLoading?: boolean;
}

const { visible, isSignInRedirectLoading = false } = defineProps<Props>();

const emit = defineEmits<{
  continue: [];
}>();

const SESSION_EXPIRED_DIALOG_TITLE = 'Session Expired';
const SESSION_EXPIRED_DIALOG_MESSAGE =
  'For security reasons, your session has expired due to inactivity. Please sign in again to access your account.';

const isContinueDisabled = computed(() => {
  return isSignInRedirectLoading;
});

const handleContinue = () => {
  if (isSignInRedirectLoading) {
    return;
  }

  emit('continue');
};
</script>

<template>
  <UiDialogShell
    :visible="visible"
    max-width="calc(100vw - 2rem)"
    :dismissable-mask="false"
    :close-on-escape="false"
    labelled-by="session-expired-dialog-title"
    :pt="{
      mask: {
        class: 'session-expired-dialog__mask',
      },
    }"
  >
    <template #surface>
      <div class="session-expired-dialog__container">
        <UiDialogCloseButton
          :disabled="isContinueDisabled"
          label="Continue to sign in"
          @click="handleContinue"
        />

        <div class="session-expired-dialog__content">
          <h2 id="session-expired-dialog-title" class="session-expired-dialog__title">
            {{ SESSION_EXPIRED_DIALOG_TITLE }}
          </h2>

          <div class="session-expired-dialog__illustration-frame">
            <img
              :src="sessionExpiredDialogIllustration"
              alt=""
              class="session-expired-dialog__illustration"
            />
          </div>

          <p class="session-expired-dialog__message">{{ SESSION_EXPIRED_DIALOG_MESSAGE }}</p>
        </div>

        <div class="session-expired-dialog__actions">
          <Button
            label="Continue to sign in"
            rounded
            :loading="isSignInRedirectLoading"
            :disabled="isContinueDisabled"
            @click="handleContinue"
          />
        </div>
      </div>
    </template>
  </UiDialogShell>
</template>

<style scoped lang="scss">
:global(.session-expired-dialog__mask) {
  padding: $space-md;
  background: rgba(15, 23, 42, 0.48);
  backdrop-filter: blur(4px);
}

.session-expired-dialog {
  &__container {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: $space-xl;
    min-height: 24.875rem;
    padding: 5rem 2.5rem 2.5rem;
  }

  &__content {
    position: relative;
    z-index: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: $space-md;
    width: 100%;
  }

  &__title {
    margin: 0;
    color: var(--p-surface-700);
    font-size: 1.5rem;
    font-weight: $font-semibold;
    line-height: 1;
    text-align: center;
  }

  &__illustration-frame {
    position: relative;
    display: flex;
    justify-content: center;
    width: 100%;
    height: 6.25rem;
    border-radius: 1rem;
    background: var(--p-surface-100);
  }

  &__illustration {
    width: 9rem;
    height: 9rem;
    margin-top: -1.375rem;
    object-fit: contain;
  }

  &__message {
    margin: 0;
    width: 16.25rem;
    color: var(--p-surface-500);
    font-size: $text-sm;
    font-weight: $font-medium;
    line-height: 1;
    text-align: center;
  }

  &__actions {
    position: relative;
    z-index: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;

    :deep(.p-button) {
      min-height: 2rem;
      padding: 0.4375rem 0.65625rem;
      font-size: $text-sm;
      font-weight: $font-medium;
    }
  }
}
</style>
